import { useState } from "react";
import {
  ArrowRight, CheckCircle, CircleDot, Send, Loader2,
} from "lucide-react";
import { CTA_SECTION, SERVICE_OPTIONS } from "../data/mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CTASection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    phone: "",
    email: "",
    service: "",
    pickupLocation: "",
    deliveryLocation: "",
    description: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone || !formData.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success("Quote request submitted! We'll respond within 1 hour.");
    } catch (err) {
      console.error("Quote submission error:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-20 lg:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #070e24 0%, #0c1839 30%, #11214d 55%, #0c1839 80%, #070e24 100%)' }}>
      {/* Decorative */}
      <div className="absolute top-20 right-0 w-80 h-80 rounded-full opacity-20 hidden md:block" style={{ background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)' }} />
      <div className="absolute bottom-20 left-0 w-96 h-96 rounded-full opacity-15 hidden md:block" style={{ background: 'radial-gradient(circle, rgba(30,64,175,0.15) 0%, transparent 70%)' }} />
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="lg:pt-4">
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-white leading-tight mb-6">
              YOUR FREIGHT ISN'T GOING TO{" "}
              <span className="text-hevi-red">MOVE ITSELF</span>
            </h2>
            <p className="text-white/50 text-[16px] leading-relaxed mb-9">
              {CTA_SECTION.subheadline}
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-10">
              {CTA_SECTION.benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span className="text-white/65 text-[14.5px]">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Urgency */}
            <div className="inline-flex items-center gap-2.5 bg-emerald-500/[0.1] border border-emerald-500/20 rounded-xl px-5 py-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-emerald-300 font-semibold text-sm">
                {CTA_SECTION.urgency}
              </span>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-7 sm:p-9 shadow-2xl shadow-black/20 border border-white/80">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={30} className="text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-3">Quote Request Sent!</h3>
                <p className="text-slate-400 text-sm">We'll get back to you within 1 hour during business hours.</p>
                <Button
                  onClick={() => { setSubmitted(false); setFormData({ fullName: "", companyName: "", phone: "", email: "", service: "", pickupLocation: "", deliveryLocation: "", description: "" }); }}
                  className="mt-6 bg-navy-800 hover:bg-navy-700 text-white rounded-xl"
                >
                  Submit Another Quote
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 className="text-xl font-bold text-navy-900 mb-1">Get Your Free Quote</h3>
                <p className="text-slate-400 text-sm mb-7">Takes less than 2 minutes</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                    <Input
                      value={formData.fullName}
                      onChange={(e) => handleChange("fullName", e.target.value)}
                      placeholder="John Doe"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Company Name</label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => handleChange("companyName", e.target.value)}
                      placeholder="ABC Industries"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Phone *</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Email *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="john@company.com"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Service Needed</label>
                  <Select value={formData.service} onValueChange={(val) => handleChange("service", val)}>
                    <SelectTrigger className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue text-sm">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Pickup Location</label>
                    <Input
                      value={formData.pickupLocation}
                      onChange={(e) => handleChange("pickupLocation", e.target.value)}
                      placeholder="City, Province"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Delivery Location</label>
                    <Input
                      value={formData.deliveryLocation}
                      onChange={(e) => handleChange("deliveryLocation", e.target.value)}
                      placeholder="City, Province/State"
                      className="h-11 border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 text-sm"
                    />
                  </div>
                </div>

                <div className="mb-7">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Load Description / Special Requirements</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Describe your load, dimensions, weight, special handling..."
                    className="border-slate-200/80 rounded-lg focus:border-hevi-blue focus:ring-hevi-blue/15 min-h-[85px] text-sm"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-hevi-red hover:bg-red-700 text-white font-bold text-[15px] h-13 rounded-xl shadow-xl shadow-red-500/15 hover:shadow-red-500/30 transition-all duration-300 group disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      SUBMITTING...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      GET MY FREE QUOTE
                      <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
